import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import { useFormik } from "formik";
import xCloseIMG from "img/icons/actions/x-close.png";

import eyeOpenImg from "img/icons/misc/eye-on.svg";
import eyeCloseImg from "img/icons/misc/eye-off.svg";
import errorIcon from "img/icons/status/alert-circle.png";

import "./ModalV2.sass";

ReactModal.setAppElement("#root");

/**
 * field example:
 * {
 *   name: "code",
 *   type: "text" | "password" | "otp" | "numeric",
 *   label?: string,
 *   placeholder?: string,
 *   disabled?: boolean,
 *   required?: boolean,
 *   maxLength?: number,
 *   onlyDigits?: boolean,
 *   inputMode?: string,
 *   autoComplete?: string,
 *   externalError?: string,   // server error
 *   onValueChange?: (nextValue, formik) => void,
 * }
 */

export default function ModalV2({
  open,
  onClose,
  title,
  description,
  content,
  afterFields,
  footer,
  fields,
  validationSchema,
  initialValues,
  onSubmit,
  titleGap = 16,
  width = 560,
  padding = 24,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  showClose = true,
  className = "",
}) {
  const hasFields = Array.isArray(fields) && fields.length > 0;

  const [visiblePasswords, setVisiblePasswords] = useState({});

  const togglePassword = (name) => {
    setVisiblePasswords((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const formik = useFormik({
    initialValues: initialValues || {},
    validationSchema: hasFields ? validationSchema : undefined,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, helpers) => {
      if (!hasFields) return;

      try {
        await onSubmit?.(values, helpers);
      } catch (error) {
        console.error("Form submission error:", error);
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setVisiblePasswords({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const shouldShowYupError = (name) =>
    (formik.touched[name] || formik.submitCount > 0) && Boolean(formik.errors[name]);

  const getErrorText = (field) => {
    const name = field.name;
    const yupError = shouldShowYupError(name) ? formik.errors[name] : "";
    const ext = field.externalError || "";
    return ext || yupError || "";
  };

  const applyDigitsFilter = (value, maxLength) => {
    const next = String(value ?? "").replace(/\D/g, "");
    return typeof maxLength === "number" ? next.slice(0, maxLength) : next;
  };

  const renderInput = (field) => {
    const {
      name,
      label,
      type = "text",
      placeholder,
      disabled,
      required,
      maxLength,
      onlyDigits,
      inputMode,
      autoComplete,
      onValueChange,
    } = field;

    const errorText = getErrorText(field);
    const fieldHasError = Boolean(errorText);

    const value = formik.values[name] ?? "";

    const isPassword = type === "password";
    const isOtp = type === "otp" || type === "numeric";
    const isDigits = Boolean(onlyDigits || isOtp);

    const finalInputType = isPassword
      ? visiblePasswords[name]
        ? "text"
        : "password"
      : "text";

    const finalMaxLength =
      typeof maxLength === "number" ? maxLength : isOtp ? 8 : undefined;

    const finalInputMode = inputMode || (isOtp ? "numeric" : undefined);

    const finalAutoComplete =
      autoComplete || (isPassword ? "new-password" : isOtp ? "one-time-code" : "off");

    const handleChange = (e) => {
      let next = e.target.value;

      if (isDigits) next = applyDigitsFilter(next, finalMaxLength);
      else if (typeof finalMaxLength === "number") next = String(next).slice(0, finalMaxLength);

      formik.setFieldValue(name, next, true);
      onValueChange?.(next, formik);
    };

    return (
      <div key={name} className={`ModalV2__field ${fieldHasError ? "ModalV2__field--error" : ""}`}>
        {label && (
          <div className="ModalV2__label">
            {label}
            {required ? <span className="ModalV2__required">*</span> : null}
          </div>
        )}

        <div className="ModalV2__controlWrap">
          <input
            className="ModalV2__input"
            name={name}
            id={name}
            type={finalInputType}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onBlur={() => formik.setFieldTouched(name, true, true)}
            disabled={disabled}
            inputMode={finalInputMode}
            maxLength={finalMaxLength}
            aria-invalid={fieldHasError}
            autoComplete={finalAutoComplete}
          />

          {isPassword ? (
            <button
              type="button"
              className="ModalV2__rightBtn"
              onClick={() => togglePassword(name)}
              aria-label={visiblePasswords[name] ? "Hide password" : "Show password"}
            >
              <img
                className="ModalV2__rightIcon"
                src={visiblePasswords[name] ? eyeOpenImg : eyeCloseImg}
                alt=""
                aria-hidden="true"
              />
            </button>
          ) : fieldHasError ? (
            <div className="ModalV2__rightIconBox" aria-hidden="true">
              <img className="ModalV2__rightIcon" src={errorIcon} alt="" />
            </div>
          ) : null}
        </div>

        <div className="ModalV2__errorText">
          {fieldHasError ? errorText : "\u00A0"}
        </div>
      </div>
    );
  };

  const body = hasFields ? (
    <form className="ModalV2__form" onSubmit={formik.handleSubmit}>
      {fields.map(renderInput)}
      {afterFields
        ? <div className="ModalV2__afterFields">{typeof afterFields === "function" ? afterFields(formik) : afterFields}</div>
        : null}

      <button type="submit" style={{ display: "none" }} aria-hidden="true" />
    </form>
  ) : content ? (
    <div className="ModalV2__body">{content}</div>
  ) : null;


  return (
    <ReactModal
      isOpen={open}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={closeOnOverlayClick}
      shouldCloseOnEsc={closeOnEsc}
      className={`ModalV2 ${className}`}
      overlayClassName="ModalV2Overlay"
      ariaHideApp={true}
    >
      <div className="ModalV2__card" style={{ width: typeof width === "number" ? `${width}px` : width }}>
        {showClose && (
          <button type="button" className="ModalV2__close" onClick={onClose} aria-label="Close">
            <img src={xCloseIMG} alt="Close" />
          </button>
        )}

        <div className="ModalV2__content" style={{ padding: typeof padding === "number" ? `${padding}px` : padding }}>
          {(title || description) && (
            <div className="ModalV2__top" style={{ gap: typeof titleGap === "number" ? `${titleGap}px` : titleGap }}>
              {title && <div className="ModalV2__title">{title}</div>}
              {description && <div className="ModalV2__description">{description}</div>}
            </div>
          )}

          {body}

          {footer && <div className="ModalV2__footer">{typeof footer === "function" ? footer(formik) : footer}</div>}
        </div>
      </div>
    </ReactModal>
  );
}
